/**
 * Path: packages/frontend/src/core/services/api.ts
 * Task: Implements a fully typed frontend HTTP client for backend API communication.
 * Update: Added ADR-002 CBAC permissions, roles matrix, and relational project members API endpoints.
 */

export interface RecentProject {
  id: string;
  title: string;
}

export interface User {
  id: string;
  email: string;
  password?: string;
  name: string;
  createdAt: string;
  recentProjects: RecentProject[];
  visibilityMode?: string;
  isSidebarMinimized?: boolean;
  systemRole?: string;
  effectiveCapabilities?: string[];
}

export interface FormSectionData {
  id: string;
  name_ger: string;
  name_en: string;
  formRank: number;
}

export interface ValueTypeData {
  value: string;
  title: string;
  icon: string;
}

export interface AppData {
  version: string;
  appAdmin: string[];
  lastModified?: string;
  valueTypes?: ValueTypeData[];
  formSections?: FormSectionData[];
  [key: string]: any;
}

export interface RoleRecord {
  id: string;
  scope: 'GLOBAL' | 'PROJECT';
  name_en: string;
  name_ger: string;
  description: string | null;
  is_system: boolean;
  capabilities: string[];
}

export interface CapabilityRecord {
  id: string;
  name_en: string;
  name_ger: string;
  description: string | null;
  category: 'app' | 'project' | 'workspace' | 'comments';
}

export interface ProjectMemberRecord {
  projectId: string;
  userId: string;
  name: string;
  email: string;
  roleId: string;
  roleNameEn: string;
  roleNameGer: string;
  createdAt: string;
}

export interface ProjectPermissions {
  reader: string[];
  author: string[];
  projectAdmin: string[];
}

export interface ProjectStats {
  entityCounts: Record<string, number>;
  fileSize: number;
  warnings: Record<string, number>;
  mostCriticalNodes: Array<{ id: string; title: string; type: string; successorCount: number }>;
  mostComplexNodes: Array<{ id: string; title: string; type: string; predecessorCount: number }>;
  numericStats?: Record<string, Record<string, { min: number; max: number; avg: number }>>;
}

export interface ProjectMetadata {
  displayName: string;
  summary: string;
  createdAt: string;
  visibility: string;
  permissions: ProjectPermissions;
  stats?: ProjectStats;
  color?: string;
  icon?: string;
  iconShape?: string;
  fieldSettings?: Record<string, { isRequired?: boolean }>;
  entityTypes?: string[];
}

export interface ApiNode {
  id: string;
  type: string;
  title: string;
  version?: string;
  lastUpdated?: string;
  updatedBy?: string;
  rank?: number;
  color?: string;
  icon?: string;
  system?: string;
  history?: any[];
  comments?: any[];
  attributes: Record<string, any>;
}

export interface ApiEdge {
  source: string;
  target: string;
  relationId: string;
  meta?: Record<string, any>;
}

export interface DatastoreWrapper {
  id?: string;
  name?: string;
  nodes?: ApiNode[];
  edges?: ApiEdge[];
  periods?: Record<string, { label: string; weeks: number }>;
  lastModified?: string;
}

export interface CommentData {
  id?: string;
  text: string;
  author?: string;
  datetime?: string;
}

interface ApiResponse {
  success: boolean;
  message?: string;
  [key: string]: any;
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(url: string, options?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(url, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(options?.headers || {})
      },
      ...options
    });
  } catch (networkError) {
    throw new ApiError(0, 'Network error: Backend server might be unreachable.');
  }

  const rawText = await response.text();
  let data: ApiResponse;
  if (!rawText) {
    if (!response.ok) {
      throw new ApiError(response.status, `HTTP Error ${response.status}: Empty response from server.`);
    }
    data = { success: true };
  } else {
    try {
      data = JSON.parse(rawText);
    } catch (parseError) {
      throw new ApiError(response.status, `Invalid JSON response from server. Status: ${response.status}. Preview: ${rawText.substring(0, 100)}...`);
    }
  }

  if (!response.ok) {
    throw new ApiError(response.status, data.message || `HTTP Error ${response.status}`);
  }

  if (data && typeof data === 'object' && !data.success && data.success !== undefined) {
    throw new ApiError(response.status, data.message || 'API operation failed without error message.');
  }

  return data as unknown as T;
}

export const api = {
  auth: {
    async login(credentials: Record<string, string>): Promise<{ user: User }> {
      return request<{ user: User }>('/api/login', {
        method: 'POST',
        body: JSON.stringify(credentials)
      });
    },
    async logout(): Promise<void> {
      return request<void>('/api/logout', { method: 'POST' });
    },
    async getSession(projectId?: string): Promise<{ user: User | null }> {
      const query = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
      return request<{ user: User | null }>(`/api/session${query}`);
    }
  },

  user: {
    async register(userData: Record<string, string>): Promise<void> {
      return request<void>('/api/register', {
        method: 'POST',
        body: JSON.stringify(userData)
      });
    },
    async getList(): Promise<{ users: User[] }> {
      return request<{ users: User[] }>('/api/users');
    },
    async updateRecentProjects(recentProjects: any[]): Promise<{ recentProjects: any[] }> {
      return request<{ recentProjects: any[] }>('/api/user/recent', {
        method: 'POST',
        body: JSON.stringify({ recentProjects })
      });
    },
    async updateVisibilityMode(visibilityMode: string): Promise<{ visibilityMode: string }> {
      return request<{ visibilityMode: string }>('/api/user/visibility-mode', {
        method: 'POST',
        body: JSON.stringify({ visibilityMode })
      });
    },
    async updateSidebarMode(isSidebarMinimized: boolean): Promise<{ isSidebarMinimized: boolean }> {
      return request<{ isSidebarMinimized: boolean }>('/api/user/sidebar-mode', {
        method: 'POST',
        body: JSON.stringify({ isSidebarMinimized })
      });
    },
    async deleteSelf(): Promise<void> {
      return request<void>('/api/user/delete', { method: 'POST' });
    },
    async changePassword(passwords: Record<string, string>): Promise<void> {
      return request<void>('/api/user/password', {
        method: 'POST',
        body: JSON.stringify(passwords)
      });
    },
    async updateProfile(name: string): Promise<void> {
      return request<void>('/api/user/profile', {
        method: 'POST',
        body: JSON.stringify({ name })
      });
    }
  },

  permissions: {
    async getCapabilities(): Promise<{ capabilities: CapabilityRecord[] }> {
      return request<{ capabilities: CapabilityRecord[] }>('/api/admin/capabilities');
    },
    async getRoles(): Promise<{ roles: RoleRecord[] }> {
      return request<{ roles: RoleRecord[] }>('/api/admin/roles');
    },
    async updateRoleCapabilities(roleId: string, capabilityIds: string[]): Promise<{ role: RoleRecord }> {
      return request<{ role: RoleRecord }>(`/api/admin/roles/${encodeURIComponent(roleId)}/capabilities`, {
        method: 'PUT',
        body: JSON.stringify({ capabilityIds })
      });
    },
    async getProjectMembers(projectId: string): Promise<{ members: ProjectMemberRecord[] }> {
      return request<{ members: ProjectMemberRecord[] }>(`/api/projects/${encodeURIComponent(projectId)}/members`);
    },
    async upsertProjectMember(projectId: string, userId: string, roleId: string): Promise<{ members: ProjectMemberRecord[] }> {
      return request<{ members: ProjectMemberRecord[] }>(`/api/projects/${encodeURIComponent(projectId)}/members/${encodeURIComponent(userId)}`, {
        method: 'PUT',
        body: JSON.stringify({ roleId })
      });
    },
    async removeProjectMember(projectId: string, userId: string): Promise<void> {
      return request<void>(`/api/projects/${encodeURIComponent(projectId)}/members/${encodeURIComponent(userId)}`, {
        method: 'DELETE'
      });
    },
    async getProjectCapabilities(projectId: string): Promise<{ effectiveCapabilities: string[] }> {
      return request<{ effectiveCapabilities: string[] }>(`/api/projects/${encodeURIComponent(projectId)}/capabilities`);
    }
  },

  admin: {
    async deleteUser(email: string): Promise<void> {
      return request<void>('/api/admin/user/delete', {
        method: 'POST',
        body: JSON.stringify({ email })
      });
    },
    async updateUserRole(email: string, systemRole: string): Promise<void> {
      return request<void>('/api/admin/user/role', {
        method: 'POST',
        body: JSON.stringify({ email, systemRole })
      });
    },
    async deleteProject(projectId: string): Promise<void> {
      return request<void>('/api/admin/project/delete', {
        method: 'POST',
        body: JSON.stringify({ projectId })
      });
    }
  },

  app: {
    async getData(): Promise<{ appData: AppData }> {
      return request<{ appData: AppData }>('/api/app/data');
    },
    async saveData(data: AppData): Promise<void> {
      return request<void>('/api/app/data', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },
    async getGlobalStats(): Promise<{ stats: Record<string, number> }> {
      return request<{ stats: Record<string, number> }>('/api/stats');
    }
  },

  project: {
    async getMetadata(): Promise<{ projectsMetadata: Record<string, ProjectMetadata> }> {
      return request<{ projectsMetadata: Record<string, ProjectMetadata> }>('/api/projects/metadata');
    },
    async getStats(projectId: string): Promise<{ project: ProjectMetadata }> {
      return request<{ project: ProjectMetadata }>(`/api/project/${projectId}/stats`);
    },
    async updateMetadata(projectId: string, updates: Partial<ProjectMetadata>): Promise<{ project: ProjectMetadata }> {
      return request<{ project: ProjectMetadata }>(`/api/project/${projectId}/metadata`, {
        method: 'POST',
        body: JSON.stringify(updates)
      });
    },
    async getTemplates(): Promise<{ templates: Record<string, ProjectMetadata> }> {
      return request<{ templates: Record<string, ProjectMetadata> }>('/api/projects/templates');
    },
    async create(payload: { id: string; displayName: string; summary: string; visibility: string; schemeId: string }): Promise<{ projectId: string }> {
      return request<{ projectId: string }>('/api/projects/create', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
    },
    async getPermissions(): Promise<{ currentUserEmail: string; appData: { appAdmin: string[] }; projectPermissions: any }> {
      return request<{ currentUserEmail: string; appData: { appAdmin: string[] }; projectPermissions: any }>('/api/permissions');
    }
  },

  datastore: {
    async get(projectId: string): Promise<{ datastore: any; periods: any; nodes: ApiNode[]; edges: ApiEdge[] }> {
      return request<{ datastore: any; periods: any; nodes: ApiNode[]; edges: ApiEdge[] }>(`/api/datastore/${projectId}`);
    },
    async save(projectId: string, payload: DatastoreWrapper): Promise<void> {
      return request<void>(`/api/datastore/${projectId}`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });
    }
  },

  comments: {
    async add(projectId: string, entityType: string, entityId: string, payload: CommentData): Promise<{ comment: CommentData }> {
      return request<{ comment: CommentData }>(`/api/datastore/${projectId}/entity/${entityType}/${entityId}/comments`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });
    },
    async update(projectId: string, entityType: string, entityId: string, commentId: string, text: string): Promise<{ comment: CommentData }> {
      return request<{ comment: CommentData }>(`/api/datastore/${projectId}/entity/${entityType}/${entityId}/comments/${commentId}`, {
        method: 'PUT',
        body: JSON.stringify({ text })
      });
    },
    async delete(projectId: string, entityType: string, entityId: string, commentId: string): Promise<void> {
      return request<void>(`/api/datastore/${projectId}/entity/${entityType}/${entityId}/comments/${commentId}`, {
        method: 'DELETE'
      });
    }
  }
};
